import { Empty, FoldablePanel, Note } from "./Panel";
import { useMispricedGrid } from "../hooks/useMispriced";
import { sourceLabel } from "../lib/mispriced";
import { zTone, zWord } from "../lib/zscore";
import type { MispricedGridCell } from "../api/types";
import {
  FRESHNESS,
  cellOf,
  freshHint,
  freshLabel,
  roi,
  roiWord,
  sourcesOf,
  staleShare,
  stepsOf,
  truncated,
  verdict,
} from "../lib/mispricedgrid";

/**
 * Сетка порога кромки: что дала бы каждая ступень `MISPRICED_TAKER_MARGIN`.
 *
 * # Зачем панель на экране
 *
 * Порог 0.02 подписан в `.env.example` прямым текстом как «отправная точка
 * оператора, а не замер», и первый же вопрос по панели расхождения — не мал ли
 * он. Сводка ответить не может: она считает один порог, тот, что стоял. До сих
 * пор ответ жил только в офлайн-гейте (`make gate-mispricing`), то есть был
 * доступен тому, у кого под рукой туннель к боевой базе.
 *
 * # Почему таблица ДВУМЕРНАЯ
 *
 * Одномерная врёт, и врёт убедительно. Замер 2026-09-01, 11 400 сведённых
 * тейкерских строк: сетка без разреза по возрасту снимка растёт монотонно —
 * ROI +6 % на ступени 0.02 и +62 % на 0.20 — и читается как «поднимай порог».
 * Весь этот рост лежит в снимках старше 200 мс, а на свежих каждая ступень
 * отрицательна и портится с ростом порога (−5.8 % на 0.02, −25.7 % на 0.20).
 *
 * Порог отбирает не кромку, а нашу ЗАДЕРЖКУ: доля протухших снимков среди
 * срабатываний растёт с 5.6 % на ступени 0.02 до 49.1 % на 0.20. Поэтому
 * колонка «протухших» стоит в таблице рядом со счётом, а не в сноске: без неё
 * оператор увидел бы ровно тот вывод, который замер и опроверг.
 *
 * # Почему один тейкер
 *
 * Живьём торгует он один (ADR-0022), и порог у него ЧИСЛО. У мейкера порог —
 * пара «выставить / снять», и ступень одномерной лестницы её не описывает:
 * половина ответа там в разрыве между порогами. Пара калибруется своим
 * разделом гейта.
 *
 * # Почему панель складная
 *
 * Тело FoldablePanel не монтируется, пока панель свёрнута, — значит и хук
 * молчит. Сетка отвечает на вопрос о калибровке, а его задают раз в несколько
 * дней; в такте опроса сводки ей делать нечего.
 */
export function MispricedGridPanel({
  hours,
  windowLen,
}: {
  hours: number;
  windowLen: number;
}) {
  return (
    <FoldablePanel
      title="Сетка порога кромки"
      subtitle={`за ${hours} ч, тейкер: что дала бы каждая ступень`}
    >
      <MispricedGridBody hours={hours} windowLen={windowLen} />
    </FoldablePanel>
  );
}

function MispricedGridBody({ hours, windowLen }: { hours: number; windowLen: number }) {
  const grid = useMispricedGrid(hours, windowLen);
  const cells = grid.data?.cells ?? [];
  const sources = sourcesOf(cells);

  if (!grid.data) {
    return <Empty>{grid.isError ? "сетка не загрузилась" : "загрузка…"}</Empty>;
  }
  if (cells.length === 0) {
    return (
      <Empty>
        тейкерских заявок за срок не было: сетка считается по журналу, а он
        пополняется, только когда кромка перешагнула стоящий порог
      </Empty>
    );
  }
  return (
    <>
      <div className="flex flex-col gap-5">
        {sources.map((source) => (
          <SourceGrid
            key={source}
            source={source}
            cells={cells}
            marginMax={grid.data.margin_max}
            marginMin={grid.data.margin_min}
          />
        ))}
      </div>
      <Note>
        В клетке — <b>ROI ступени</b>: счёт, делённый на затраты по плану, и
        число заявок в скобках. Именно отношение, а не сумма: входов у ступеней
        отличается на порядок, и по суммам самый редкий порог всегда выглядел бы
        лучшим. Ступени <b>вложены</b> — заявка с кромкой 0.12 считается в каждой
        ступени до 0.10, — поэтому столбцы не складываются ни во что.
        {" "}
        <b>Судить о деньгах можно по колонке «свежий»</b> и только по ней:
        живой вход берёт снимки моложе 50 мс, а на протухших мы «покупаем» по
        цене, которой на бирже уже нет. Колонка «протухших» рядом со счётом
        показывает, что именно отбирает ступень: если она растёт вместе с
        порогом, то кромку даёт наша задержка, а не рынок.
        {" "}
        Зелёный ROI без <b>двух сигм запаса</b> не значит ничего: максимум по
        лестнице достаётся самой редкой ступени просто за редкость, и приговор
        над таблицей такие ступени не называет.
      </Note>
    </>
  );
}

/**
 * Сетка одного источника.
 *
 * Порознь, а не в одной таблице с колонкой источника: `pm` и `model` — два
 * ответа на вопрос «стоит ли достройка своих секунд», и среднее между ними не
 * описывает ни одного. То же правило, по которому режется сводка.
 *
 * Экспортируется РАДИ ТЕСТА, и это не послабление. Хуков внутри нет, поэтому
 * таблица рендерится в разметку без сети и без DOM — а проверять надо именно
 * разметку: «функция посчитала запас» и «запас виден рядом со счётом» разные
 * утверждения, и панель без второго звала бы поднимать порог по зелёному ROI,
 * под которым нет двух сигм. Тот же довод и тот же приём, что у RateCell.
 */
export function SourceGrid({
  source,
  cells,
  marginMax,
  marginMin,
}: {
  source: string;
  cells: MispricedGridCell[];
  marginMax: string;
  marginMin: string;
}) {
  const mine = cells.filter((c) => c.source === source);
  const steps = stepsOf(mine);
  return (
    <div>
      <h4 className="text-xs font-medium">
        {sourceLabel(source)}{" "}
        <span className="font-normal text-[var(--color-muted)]">
          — {verdict(cells, marginMax, source)}
        </span>
      </h4>
      <div className="mt-1 overflow-x-auto">
        <table className="text-[11px]">
          <thead className="text-left text-[var(--color-muted)]">
            <tr>
              <th className="pb-1 pr-4 font-normal">ступень</th>
              {FRESHNESS.map((f) => (
                <th key={f} className="pb-1 pr-4 text-right font-normal" title={freshHint(f)}>
                  {freshLabel(f)}
                </th>
              ))}
              <th
                className="pb-1 pr-4 text-right font-normal"
                title={
                  "запас в сигмах по СВЕЖЕЙ корзине: доля угаданного против цены входа. " +
                  "Меньше двух сигм — разница неотличима от случайной, и ступень такой " +
                  "клетки приговор не называет"
                }
              >
                запас
              </th>
              <th
                className="pb-1 text-right font-normal"
                title={
                  "доля снимков старше 200 мс среди срабатываний ступени. " +
                  "Растёт с порогом — значит порог отбирает нашу задержку, а не кромку"
                }
              >
                протухших
              </th>
            </tr>
          </thead>
          <tbody>
            {steps.map((step) => (
              <StepRow
                key={step}
                step={step}
                source={source}
                cells={cells}
                truncated={truncated(step, marginMax)}
                marginMin={marginMin}
                marginMax={marginMax}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StepRow({
  step,
  source,
  cells,
  truncated: cut,
  marginMin,
  marginMax,
}: {
  step: string;
  source: string;
  cells: MispricedGridCell[];
  truncated: boolean;
  marginMin: string;
  marginMax: string;
}) {
  const stale = staleShare(cells, source, step);
  const live = cellOf(cells, source, step, "live");
  return (
    <tr className="border-t border-[var(--color-border)]">
      <td className="whitespace-nowrap py-0.5 pr-4">
        <span className="num">{step}</span>
        {/* Ступень НИЖЕ реально стоявшего порога описывает не себя: журнал
            усечён, и строк с меньшей кромкой в нём не рождалось вовсе. Пометка
            обязательна — без неё повтор читается как измерение. */}
        {cut && (
          <span
            className="ml-1 text-[var(--color-muted)]"
            title={
              `журнал усечён порогом ${marginMin}…${marginMax}: строк с кромкой ниже ` +
              `него не рождалось вовсе. Эта ступень повторяет ${marginMax}, а не меряет себя`
            }
          >
            ↑
          </span>
        )}
      </td>
      {FRESHNESS.map((f) => {
        const cell = cellOf(cells, source, step, f);
        const r = roi(cell);
        return (
          <td
            key={f}
            className={`num whitespace-nowrap py-0.5 pr-4 text-right ${roiTone(f, r)}`}
            title={cell ? `счёт ${cell.pnl} по ${cell.priced}, кромка ${cell.edge_median}` : undefined}
          >
            {roiWord(cell)}
            {cell && (
              <span className="ml-1 text-[var(--color-muted)]">({cell.signals})</span>
            )}
          </td>
        );
      })}
      {/* Запас — по СВЕЖЕЙ корзине и только по ней: на протухшем снимке
          уверенно значим любой порог, и колонка мерила бы нашу задержку. */}
      <td
        className="num py-0.5 pr-4 text-right"
        style={{ color: zTone(live?.z) }}
        title={
          "доля угаданного против цены входа на свежих снимках. " +
          "Зелёное — только от двух сигм: на меньшем разница неотличима от случайной"
        }
      >
        {zWord(live?.z)}
      </td>
      <td className="num py-0.5 text-right text-[var(--color-muted)]">
        {stale === null ? "—" : `${stale.toFixed(0)} %`}
      </td>
    </tr>
  );
}

/**
 * Цвет клетки счёта. Красится ЗНАК, а не запас в сигмах, — тот же выбор и тот
 * же довод, что у перевеса группы: счёт и запас две разные меры, и покрась их
 * одним правилом, они выглядели бы одной. Запас стоит своей колонкой рядом, и
 * планка двух сигм живёт там.
 *
 * Зелёным красится ТОЛЬКО свежая корзина, и вот это уже правило замера. Плюс на
 * протухшем снимке — не заработок, а наша задержка: там мы «покупаем» по цене,
 * которой на бирже уже нет. Покрась его зелёным, и панель звала бы поднимать
 * порог ровно там, где замер показал обратное.
 */
function roiTone(freshness: string, r: number | null): string {
  if (r === null) return "text-[var(--color-muted)]";
  if (freshness !== "live") return "text-[var(--color-muted)]";
  if (r > 0) return "text-[var(--color-good)]";
  if (r < 0) return "text-[var(--color-bad)]";
  return "";
}
